import dayjs from 'dayjs';
import { alert } from '@/services/alert';
import { useNavigate, useSearchParams } from 'react-router';
import { useDebouncedValue } from '@mantine/hooks';
import { SearchInput } from '@/components/SearchInput';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { usePatientList } from './hooks/usePatientList';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { FilterOption, PatientListPageDataRow } from './types';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import DosespotConfigModal from '@/components/DosespotConfigModal';
import { downloadAttachmentBlob } from '@/services/attachments';
import { Button, Container, Group, Paper, Stack, Title } from '@mantine/core';
import { isMedplumRateLimitError, getRateLimitAlert } from '@/utils/medplumRateLimit';
import { MantineReactTable, useMantineReactTable, MRT_SortingState } from 'mantine-react-table';
import { FEATURE_FLAGS, PatientPageFilterOption, PATIENT_PAGE_FILTER_OPTIONS } from '@bariendo/lib/constants';
import { CarrumReportingBanner } from './CarrumReportingBanner';
import { useMedplum } from '@medplum/react';
import { rowsToCsv } from '@/services/csv';
import { InquiryTab } from './components/InquiryTab';
import { initialListFilter, LIST_FILTER_PARAM, rememberListFilter } from './services/listFilterPersistence';
import { PreConsultationTab } from './components/PreConsultationTab';
import { PostConsultationTab } from './components/PostConsultationTab';
import { CARRUM_CSV_HEADERS, carrumRowToCsvCells, fetchAllCarrumRows } from './carrum';
import { CoverageHub } from './coverage-hub/CoverageHub';
import { IntakeHub } from './intake/IntakeHub';
import { intakeTabForFilter, resolveListFilter, visibleFilterOptions } from './services/filterOptions';

import './PatientListPage.css';

dayjs.extend(isSameOrBefore);

// The prototype's active pill. Only the hub pills (Intake, Coverage) take it for now; the rest follow separately.
const HUB_PILL_ACTIVE_STYLE: CSSProperties = {
  backgroundColor: 'var(--ev-color-green-dark, #243721)',
  color: 'var(--ev-color-warm-white, #fafcf7)',
  fontWeight: 500,
};

const ALL_FILTER_OPTIONS: FilterOption[] = [
  { label: 'All patients', value: PATIENT_PAGE_FILTER_OPTIONS.ALL, physicianRestricted: true },
  { label: 'My patients', value: PATIENT_PAGE_FILTER_OPTIONS.MY_PATIENTS },
  { label: 'My upcoming appointments', value: PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS },
  {
    label: 'Intake',
    value: PATIENT_PAGE_FILTER_OPTIONS.INTAKE,
    featureFlag: FEATURE_FLAGS.ENABLE_INTAKE_HUB,
    physicianRestricted: true,
  },
  {
    label: 'Coverage',
    value: PATIENT_PAGE_FILTER_OPTIONS.COVERAGE,
    featureFlag: FEATURE_FLAGS.ENABLE_COVERAGE_HUB,
    physicianRestricted: true,
  },
  {
    label: 'All inquiry',
    value: PATIENT_PAGE_FILTER_OPTIONS.ALL_INQUIRY,
    featureFlagCheck: true,
    physicianRestricted: true,
    replacedByIntakeHub: true,
  },
  {
    label: 'All pre-consultation',
    value: PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION,
    featureFlagCheck: true,
    physicianRestricted: true,
    replacedByIntakeHub: true,
  },
  {
    label: 'All post-consultation',
    value: PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION,
    featureFlagCheck: true,
    physicianRestricted: true,
    replacedByIntakeHub: true,
  },
  {
    label: 'All pre-procedure',
    value: PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE,
    featureFlagCheck: true,
    physicianRestricted: true,
  },
  {
    label: 'All post-procedure',
    value: PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE,
    featureFlagCheck: true,
    physicianRestricted: true,
  },
  {
    label: 'Carrum',
    value: PATIENT_PAGE_FILTER_OPTIONS.CARRUM,
    featureFlag: FEATURE_FLAGS.ENABLE_SIGNUPS_TRACKER,
    physicianRestricted: true,
  },
];

export function PatientListPage(): ReactElement {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUserPermissions } = useCurrentUser();
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 400);
  const [showDosespotConfigModal, setShowDosespotConfigModal] = useState(false);
  const isDosespotConfigEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_DOSESPOT_CONFIG);
  const isPatientListAllFiltersEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PATIENT_LIST_ALL_FILTERS);
  const isSignupsTrackerEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_SIGNUPS_TRACKER);
  // The flag AND a hub role (BAR-2788); everyone else keeps the three stage pills.
  const isIntakeHubEnabled =
    useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_INTAKE_HUB) && currentUserPermissions.canViewIntakeHub;
  // Care ops' pill (BAR-3093): its own flag and role gate.
  const isCoverageHubEnabled =
    useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_COVERAGE_HUB) && currentUserPermissions.canViewCoverageHub;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilterState] = useState<PatientPageFilterOption>(() =>
    initialListFilter(searchParams.get(LIST_FILTER_PARAM))
  );

  // Kept in the URL and in the session so a rep who opens a chart comes back to
  // the queue they were working, not My patients (BAR-2786): back restores the
  // URL, and the nav link — which has no parameter — falls back to the session.
  const setActiveFilter = (filter: PatientPageFilterOption): void => {
    setActiveFilterState(filter);
    rememberListFilter(filter);
    setSearchParams(
      (params) => {
        params.set(LIST_FILTER_PARAM, filter);
        return params;
      },
      { replace: true }
    );
  };
  const listFilter = resolveListFilter(activeFilter, isIntakeHubEnabled, isCoverageHubEnabled);
  const isIntakeTab = listFilter === PATIENT_PAGE_FILTER_OPTIONS.INTAKE;
  const isCoverageTab = listFilter === PATIENT_PAGE_FILTER_OPTIONS.COVERAGE;
  const isHubTab = isIntakeTab || isCoverageTab;

  // Behind the hub, run what the Inquiry tab runs today; 1.6 removes this.
  const { patientList, columns, query, setData, variables, initialStateSorting, patientIdsLoading } = usePatientList(
    isHubTab ? PATIENT_PAGE_FILTER_OPTIONS.ALL_INQUIRY : listFilter,
    debouncedSearchTerm
  );

  const { isPhysicianOnly } = currentUserPermissions;
  const isInquiryTab = listFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_INQUIRY;
  const isPreConsultationTab = listFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION;
  const isPostConsultationTab = listFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION;

  const { data, loading, scrollPagination, error } = useLiveGraphQL<PatientListPageDataRow>(query, variables, {
    enablePagination: true,
    pageSize: 30,
    // A `_filter` (My patients, My upcoming appointments, Carrum) can't be a Subscription
    // criteria, so these lists don't live-update; said here rather than warned at runtime (BAR-2969).
    pauseLiveUpdates: Boolean(variables?.filter),
  });

  const [sorting, setSorting] = useState<MRT_SortingState>(initialStateSorting);
  const [prevFilter, setPrevFilter] = useState(activeFilter);

  if (prevFilter !== activeFilter) {
    setPrevFilter(activeFilter);
    setSorting(initialStateSorting);
  }

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(`The system temporarily limited how fast we can load the patients list. 
        Wait a few seconds before trying again.`),
      });
    }
  }, [error]);

  const filterOptions = useMemo(
    () =>
      visibleFilterOptions(ALL_FILTER_OPTIONS, {
        isPhysicianOnly,
        isAllFiltersEnabled: isPatientListAllFiltersEnabled,
        isSignupsTrackerEnabled,
        isIntakeHubEnabled,
        isCoverageHubEnabled,
      }),
    [isPatientListAllFiltersEnabled, isSignupsTrackerEnabled, isIntakeHubEnabled, isCoverageHubEnabled, isPhysicianOnly]
  );

  /**
   * Exports the full Carrum cohort, fetched independently of the table. The table pages 30 rows at
   * a time on scroll (`useLiveGraphQL`), so serializing its rows silently truncated the CSV to
   * whatever the user had scrolled past.
   */
  const handleExportCarrumCsv = async (): Promise<void> => {
    try {
      const allRows = await fetchAllCarrumRows(medplum);
      const csv = rowsToCsv(CARRUM_CSV_HEADERS, allRows.map(carrumRowToCsvCells));
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      downloadAttachmentBlob(url, 'carrum-patients.csv');
    } catch {
      alert.error({
        title: 'Error exporting Carrum patients',
        message: 'An error occurred while building the export. Please try again later.',
      });
    }
  };

  useEffect(() => {
    if (data) {
      setData(error ? [] : data);
      return;
    }

    if (error) {
      setData([]);
    }
  }, [data, error]);

  const table = useMantineReactTable({
    columns,
    data: patientList,
    rowCount: data.length,
    globalFilterFn: 'contains',
    initialState: {
      density: 'xs' as const,
    },
    state: {
      sorting,
      isLoading: loading || patientIdsLoading,
      showGlobalFilter: false,
    },
    onSortingChange: setSorting,
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: true,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (row.original.id) {
          navigate(`/Patient/${row.original.id}/timeline`);
        }
      },
      style: { cursor: row.original.id ? 'pointer' : 'default' },
    }),
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
  });

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Patients</Title>
          <Group>
            {isDosespotConfigEnabled && (
              <Button variant="outline" onClick={() => setShowDosespotConfigModal(true)}>
                Dosespot Config
              </Button>
            )}
            {/* Filters the legacy lists only; the hub's tabs have their own filters (BAR-2982). */}
            {!isHubTab && <SearchInput value={searchTerm} onChange={(value) => setSearchTerm(value.trim())} />}
          </Group>
        </Group>

        <Group gap="xs">
          {filterOptions.map((option) => (
            <Button
              key={option.value}
              size="xs"
              radius="xl"
              variant={listFilter === option.value ? 'filled' : 'light'}
              color="gray"
              style={isHubTab && option.value === listFilter ? HUB_PILL_ACTIVE_STYLE : undefined}
              onClick={() => setActiveFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </Group>

        {listFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM && (
          <CarrumReportingBanner onExportCsv={handleExportCarrumCsv} />
        )}

        {/*
          The three PAC queues own their whole panel — scope control, chips and
          table. Each roots on its own resource (sign-up episode, intro call,
          consult) rather than on a stage-tag cohort, so they share the page
          chrome above but none of `usePatientList` below.
        */}
        {isIntakeTab ? (
          <IntakeHub defaultTab={intakeTabForFilter(activeFilter)} />
        ) : isCoverageTab ? (
          <CoverageHub />
        ) : isInquiryTab ? (
          <InquiryTab />
        ) : isPreConsultationTab ? (
          <PreConsultationTab />
        ) : isPostConsultationTab ? (
          <PostConsultationTab />
        ) : (
          <Paper style={{ flex: 1, overflow: 'hidden' }}>
            <MantineReactTable table={table} />
          </Paper>
        )}
      </Stack>

      {isDosespotConfigEnabled && (
        <DosespotConfigModal
          opened={showDosespotConfigModal}
          onClose={() => {
            setShowDosespotConfigModal(false);
          }}
        />
      )}
    </Container>
  );
}
